import { useEffect, useRef, useState, type CSSProperties } from "react";
import { modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle } from "./task-modal-shared";
import { humanizePromptName, humanizeArgName } from "../command-palette/format";
import type { McpPrompt } from "../command-palette/types";

interface PromptArgsModalProps {
  open: boolean;
  onClose: () => void;
  prompt: McpPrompt | null;
  /** Called with the collected arguments; the caller then composes + displays. */
  onSubmit: (args: Record<string, string>) => void;
}

/**
 * Sprint 3 (S3.25) — a generic argument form generated from a prompt's declared
 * `arguments`. The Cmd-K "Run prompt" flow shows this first when a prompt has
 * required args (e.g. verify-spec → page_id), then composes + opens the
 * PromptDisplayModal. Prompts with no required args skip straight to display.
 */
export function PromptArgsModal({ open, onClose, prompt, onSubmit }: PromptArgsModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValues({});
      setTimeout(() => firstRef.current?.focus(), 50);
    }
  }, [open, prompt?.name]);

  if (!open || !prompt) return null;

  const args = prompt.arguments ?? [];
  const missingRequired = args.some((a) => a.required && !values[a.name]?.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (missingRequired) return;
    // Drop empty optional args so the server sees only what was provided.
    const clean: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v && v.trim()) clean[k] = v.trim();
    }
    onSubmit(clean);
  }

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label={`Run ${humanizePromptName(prompt.name)}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={iconBox}>
            <i className="ti ti-wand" aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              {humanizePromptName(prompt.name)}
            </div>
            {prompt.description ? (
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{prompt.description}</div>
            ) : null}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {args.map((arg, idx) => (
            <div key={arg.name} style={fieldStyle}>
              <label style={labelStyle}>
                {humanizeArgName(arg.name)}
                {arg.required ? <span style={{ color: "var(--c-red-600)" }}> *</span> : null}
              </label>
              <input
                ref={idx === 0 ? firstRef : undefined}
                type="text"
                style={inputStyle}
                value={values[arg.name] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [arg.name]: e.target.value }))}
                placeholder={arg.description ?? ""}
              />
              {arg.description ? (
                <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4 }}>
                  {arg.description}
                </div>
              ) : null}
            </div>
          ))}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={missingRequired}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              Compose prompt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};
